import type { Initiative } from '../types/trackerModels';

/**
 * Shared Portfolio/Program/Project tree-building logic - factored out after the exact same
 * root/children/unreachable computation was written three times (AllInitiativesPage.tsx's
 * cascading list, OverviewPage.tsx's card of the same name, and the Gantt tier-nesting in
 * OverviewPage.tsx/HubInitiativeTracker.tsx), and one of those copies had already silently
 * drifted from the others (a plain-space vs non-breaking-space indentation bug). One
 * implementation each of "what are the roots" and "what fell out of the tree entirely" is
 * worth the import over risking that class of bug a second time.
 */

/** Groups initiatives by their parentInitiativeId - each initiative appears under exactly one
 *  key (its own parentInitiativeId), never omitted and never duplicated. Siblings are sorted by
 *  name for a stable, predictable render order. */
export function buildChildrenIndex(initiatives: Initiative[]): Map<string, Initiative[]> {
  const map = new Map<string, Initiative[]>();
  initiatives.forEach((i) => {
    if (!i.parentInitiativeId) return;
    const siblings = map.get(i.parentInitiativeId) || [];
    siblings.push(i);
    map.set(i.parentInitiativeId, siblings);
  });
  map.forEach((siblings) => siblings.sort((a, b) => a.name.localeCompare(b.name)));
  return map;
}

/** Roots are initiatives with no parent, or whose parent isn't present in this same set (e.g.
 *  filtered out, or genuinely deleted) - sorted by name. */
export function getRootInitiatives(initiatives: Initiative[]): Initiative[] {
  const ids = new Set(initiatives.map((i) => i.initiativeId));
  return initiatives
    .filter((i) => !i.parentInitiativeId || !ids.has(i.parentInitiativeId))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** Initiatives that exist in the set but are unreachable from any root - the signature of a
 *  cycle (A's parent is B, B's parent is A): neither is ever a root (each has a parent present
 *  in the set) and neither is ever visited as anyone's child either, since nothing outside the
 *  cycle points into it. Confirmed live as a real bug: without this check, the entire affected
 *  branch - the cyclic node and everything legitimately parented under it - just silently
 *  disappears from the tree, with no error and no visible sign the data still exists. */
export function getUnreachableInitiatives(
  initiatives: Initiative[],
  roots: Initiative[],
  childrenOf: Map<string, Initiative[]>,
): Initiative[] {
  const visited = new Set<string>();
  const visit = (node: Initiative) => {
    if (visited.has(node.initiativeId)) return;
    visited.add(node.initiativeId);
    (childrenOf.get(node.initiativeId) || []).forEach(visit);
  };
  roots.forEach(visit);
  return initiatives.filter((i) => !visited.has(i.initiativeId));
}
